import { Box, Stack, Typography } from "@mui/material";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
  NotificationWhenControl,
} from "generated-graphql/graphql";

import { EmailCadenceControl } from "./EmailCadenceControl";
import {
  CHANNEL_CELL_SX,
  MUTED_BY_SECTION_PARENT_COPY,
  MUTED_CELL_SX,
  mutedByChannelParentCopy,
  provenanceLabel,
} from "./preferenceProvenance";
import { PreferenceSwitch } from "./PreferenceSwitch";
import {
  draftEmailChoice,
  draftValue,
  isParentChannelOn,
  NOTIFICATION_CHANNEL_EMAIL,
  PREFERENCE_ROW_CHANNELS,
  type DraftParentSwitch,
  type EmailCadenceChoice,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";
import { WhenCell } from "./WhenCell";

/**
 * One alert's row: what it is, what it opens, a toggle per channel it supports,
 * and when it fires.
 *
 * Two rules here are the reason the row is its own component rather than inline
 * markup. A channel the notification cannot use gets no control at all, because an
 * inert switch is a question the reader has no way to answer. And a channel that is
 * muted from above keeps its switch exactly where it was stored — marked as muted,
 * still interactive, never moved to off and never disabled — because the reader is
 * looking at choices that will come back the moment the mute lifts.
 *
 * "Marked as muted" is three separate cues, because no one of them reaches
 * everybody: the cell treatment in {@link MUTED_CELL_SX}, the explanation
 * announced as the switch's description by {@link PreferenceSwitch}, and the
 * off badge the column header carries.
 */

/**
 * Column widths, shared with the header above the rows so the two cannot drift.
 * A grid rather than a table: the alert cell holds two lines of prose and has to
 * be free to wrap while the control columns stay put.
 *
 * The trailing 40px is the section header's caret lane. Every row reserves it even
 * though only the header draws in it: the columns are shared, so a lane that
 * existed in the header alone would shift its switches out of line with the ones
 * beneath them — which is exactly what happened the first time.
 *
 * The alert cell's floor is what keeps the page from collapsing. With `minmax(0,
 * 1fr)` the four fixed columns take 412px whatever the viewport is, so a narrow
 * window squeezed the alert names toward nothing and the page read as broken
 * rather than as cramped. A floor plus the scroll wrapper in
 * `AlertSectionAccordion` trades that for a horizontal scrollbar, which is a
 * thing readers know how to use.
 */
export const PREFERENCE_GRID_COLUMNS = "minmax(240px, 1fr) 76px 76px 40px";

/**
 * The width below which the grid scrolls instead of shrinking: the four columns at
 * their floor, plus the three 8px gaps between them.
 */
export const PREFERENCE_GRID_MIN_WIDTH = 240 + 76 + 76 + 40 + 3 * 8;

/**
 * `silencedByParent` is deliberately absent even though the query selects it. It
 * describes the answer the *server* resolved, so it is stale from the first switch
 * the reader flips; whether this row is being silenced is computed from the draft
 * instead. `ownEnabled` is still read, because it is what seeded that draft.
 */
export type AlertRowPreference = {
  channel: string;
  ownEnabled: boolean;
  source: NotificationPreferenceSource;
};

export type AlertRowData = {
  kind: string;
  title: string;
  description: string;
  linksTo?: string | null;
  when: {
    control: NotificationWhenControl;
    fixedLabel?: string | null;
    leadTimeDays?: number | null;
    thresholdPercent?: number | null;
  };
  preferences: readonly AlertRowPreference[];
};

export type AlertRowProps = {
  row: AlertRowData;
  /** This section's parents, one per channel. */
  sectionParentSwitches: readonly DraftParentSwitch[];
  channelParentSwitches: readonly DraftParentSwitch[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  /** True while a write is in flight; see {@link PreferenceSwitch}. */
  disabled: boolean;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
};

/** How this row's switches are named to a screen reader. */
function channelAriaLabel(title: string, channel: string): string {
  if (channel === NOTIFICATION_CHANNEL_EMAIL) {
    return `${title} email`;
  }
  return `${title} in-app`;
}

/**
 * Which switch above this row is muting the channel, in the reader's words, or
 * null when nothing is. A channel mute is named before a section mute purely so
 * the reader is pointed at the switch nearest to hand; either explanation is true
 * when both are off.
 *
 * Deliberately says nothing about the row's own position: this answers "is the
 * column muted", which is what the dimming follows. Whether the copy is the honest
 * thing to *say* about this particular row is decided by the caller.
 */
function mutedReason({
  channel,
  channelParentOn,
  sectionParentOn,
}: {
  channel: string;
  channelParentOn: boolean;
  sectionParentOn: boolean;
}): string | null {
  if (!channelParentOn) {
    return mutedByChannelParentCopy(channel);
  }
  if (!sectionParentOn) {
    return MUTED_BY_SECTION_PARENT_COPY;
  }
  return null;
}

export function AlertRow({
  row,
  sectionParentSwitches,
  channelParentSwitches,
  draft,
  scope,
  disabled,
  onToggle,
  onEmailChoice,
}: AlertRowProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
        alignItems: "center",
        columnGap: 1,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box>
        <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap">
          <Typography variant="body1" fontWeight={500}>
            {row.title}
          </Typography>
          <WhenCell when={row.when} />
        </Stack>
        {row.description && (
          <Typography variant="body2" color="text.secondary">
            {row.description}
          </Typography>
        )}
      </Box>

      {PREFERENCE_ROW_CHANNELS.map((channel) => {
        const preference = row.preferences.find((entry) => entry.channel === channel);
        // No entry means this notification has no such channel — email, in practice,
        // since none of the waste kinds can send it yet. Said explicitly rather than
        // left blank: an empty cell in a grid of switches reads as a control that
        // failed to render, and a reader cannot tell it apart from one. Still not a
        // disabled switch, which would imply there is something here to turn on.
        if (!preference) {
          return (
            <Box key={channel} sx={CHANNEL_CELL_SX}>
              <Typography variant="body2" color="text.disabled">
                N/A
              </Typography>
            </Box>
          );
        }

        // Both parents read from the draft, not from the server's answer: the reader
        // has to see a parent they just flipped take effect on the rows beneath it
        // before they save, which is the whole feedback loop of this page.
        const channelParentOn = isParentChannelOn(draft, channelParentSwitches, channel);
        const sectionParentOn = isParentChannelOn(draft, sectionParentSwitches, channel);
        const mutedFromAbove = mutedReason({ channel, channelParentOn, sectionParentOn });
        // This row's own position, read from the draft rather than from the
        // server's `silencedByParent`: the draft carries unsaved edits, and the
        // server's answer is stale the moment the reader flips anything — a switch
        // they just turned off would still be described as silenced from above.
        const ownEnabled = draftValue(draft, row.kind, channel);
        // A parent's copy promises the choices come back when the mute lifts. That
        // is only true of a row that is switched on; on a row the reader turned off
        // themselves it promises a delivery that will never happen, so the
        // explanation falls back to saying where this position came from. The
        // dimming still follows the parent alone, because the column really is
        // muted whatever this one row says.
        const explanation =
          ownEnabled && mutedFromAbove ? mutedFromAbove : provenanceLabel(preference.source, scope);

        return (
          <Box key={channel} sx={{ ...CHANNEL_CELL_SX, ...(mutedFromAbove ? MUTED_CELL_SX : {}) }}>
            {channel === NOTIFICATION_CHANNEL_EMAIL ? (
              <EmailCadenceControl
                label={channelAriaLabel(row.title, channel)}
                explanation={explanation}
                value={draftEmailChoice(draft, row.kind)}
                disabled={disabled}
                onChange={(choice) => onEmailChoice(row.kind, choice)}
              />
            ) : (
              <PreferenceSwitch
                label={channelAriaLabel(row.title, channel)}
                explanation={explanation}
                checked={ownEnabled}
                disabled={disabled}
                size="small"
                onChange={(enabled) => onToggle(row.kind, channel, enabled)}
              />
            )}
          </Box>
        );
      })}

      {/* The section header's caret lane, reserved so the columns line up. */}
      <Box />
    </Box>
  );
}
